import { useState, useEffect } from 'react';
import api from '../../services/api';
import { useI18n } from '../../context/useI18n';
import { translateTrafficLight } from '../../utils/i18nHelpers';
import VisualProgressBar from '../ui/VisualProgressBar';
import EmptyState from '../ui/EmptyState';
import { Dumbbell } from 'lucide-react';

export default function TrainingProgressPanel({ compact = false, showContinue = false, onContinue, plannedDaysOverride = null }) {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [planDayCount, setPlanDayCount] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get('/training/progress/me').then((r) => r.data?.data || null).catch(() => null),
      api.get('/training/my-current-plan').then((r) => {
        const dias = r.data?.plan?.dias;
        return Array.isArray(dias) && dias.length > 0 ? dias.length : null;
      }).catch(() => null),
    ])
      .then(([progress, days]) => {
        setData(progress);
        setPlanDayCount(days);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="card p-4 animate-pulse space-y-3">
        <div className="h-4 bg-white/10 rounded w-1/3" />
        <div className="h-2 bg-white/10 rounded w-full" />
      </div>
    );
  }

  if (!data) {
    return (
      <EmptyState
        icon={Dumbbell}
        title={t('training.progress.empty_title', 'Sin rutina activa')}
        message={t('training.progress.empty_msg', 'Cuando tu coach te asigne un plan, verás aquí tu progreso y podrás continuar donde quedaste.')}
      />
    );
  }

  const planDays = plannedDaysOverride ?? planDayCount ?? data.planned_days ?? data.planned ?? 7;
  const executed = data.executed ?? 0;
  const plannedSessions = planDays;
  const pending = Math.max(0, plannedSessions - executed);
  const adherenceLabel = t(
    'training.progress.adherence_plan_days',
    'Cumplimiento semanal ({days} días)',
    { days: planDays },
  );

  if (compact) {
    return (
      <div className="card p-4 space-y-3">
        <VisualProgressBar
          label={adherenceLabel}
          value={data.adherence_pct ?? 0}
          hint={`${translateTrafficLight(data.traffic_light, t)} · ${executed}/${plannedSessions} ${t('training.progress.sessions_short', 'sesiones')}`}
        />
        {showContinue && pending > 0 ? (
          <button type="button" className="btn-primary text-sm w-full sm:w-auto" onClick={onContinue}>
            {t('home.hero.continue_training', 'Continúa tu rutina')}
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="card space-y-3 p-4">
      <h4 className="font-bold text-[var(--d28d-text)]">{t('training.progress.title', 'Mi entrenamiento')}</h4>
      <VisualProgressBar
        label={adherenceLabel}
        value={data.adherence_pct ?? 0}
        hint={`${translateTrafficLight(data.traffic_light, t)} · ${executed}/${plannedSessions} ${t('training.progress.sessions', 'sesiones')}`}
      />
      {showContinue && pending > 0 ? (
        <button type="button" className="btn-primary text-sm" onClick={onContinue}>
          {t('home.hero.start_session', 'Iniciar sesión')}
        </button>
      ) : null}
    </div>
  );
}
